import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import hash from 'object-hash'

import { chartPalette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
} from '../utils/compass'
import { datetimeXAxis } from '../utils/constants'
import { capitalizeString, formatSeriesNames } from '../utils/string'
import { BaseContext, CommonChartConfig } from './'

const PERFORMANCE_ATTRIBUTION_CHARTS_PATH =
  '/api/charts/performance-attribution'

type ExposureChartId =
  | 'historical_exposure_by_strategy'
  | 'historical_exposure_by_sector'
  | 'historical_exposure_by_ticker'

type AttributionChartId =
  | 'unexplained_attribution'
  | 'daily_attribution'
  | 'historical_sector_exposure'
  | 'historical_sector_exposure_gross_up'

export namespace PerformanceAttribution {
  export namespace Funds {
    const CONTEXT_ID = 'equity:performance_attribution:funds'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'jgp_equity_hedge_trader_vs_bz_offshore'
        | 'jgp_equity_bz_offshore_exposure'
        | 'jgp_fia_hedge_trader_vs_bz_offshore'
        | 'jgp_fia_bz_offshore_exposure'
        | 'jgp_compounders_hedge_trader_vs_bz_offshore'
        | 'jgp_compounders_bz_offshore_exposure'
        | 'jgp_esg_fia_hedge_trader_vs_bz_offshore'
        | 'jgp_esg_fia_bz_offshore_exposure'
        | 'jgp_inst_hedge_trader_vs_bz_offshore'
        | 'jgp_inst_bz_offshore_exposure'
        | 'jgp_sula_fabio_hedge_trader_vs_bz_offshore'
        | 'jgp_sula_fabio_bz_offshore_exposure'
      context: typeof CONTEXT_ID
      params: Partial<AxiosRequestConfig>
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const getChartData = (
      config: ChartConfig,
      requestModifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (
        conf
      ) => conf
    ) =>
      getCompassAPIChartData(config, (conf) =>
        requestModifier({ ...conf, ...config.params })
      )

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'jgp_equity_hedge_trader_vs_bz_offshore':
          case 'jgp_fia_hedge_trader_vs_bz_offshore':
          case 'jgp_compounders_hedge_trader_vs_bz_offshore':
          case 'jgp_esg_fia_hedge_trader_vs_bz_offshore':
          case 'jgp_inst_hedge_trader_vs_bz_offshore':
          case 'jgp_sula_fabio_hedge_trader_vs_bz_offshore':
            return getHedgeTraderVsBzOffshoreChartProps(config, data)
          case 'jgp_equity_bz_offshore_exposure':
          case 'jgp_fia_bz_offshore_exposure':
          case 'jgp_compounders_bz_offshore_exposure':
          case 'jgp_esg_fia_bz_offshore_exposure':
          case 'jgp_inst_bz_offshore_exposure':
          case 'jgp_sula_fabio_bz_offshore_exposure':
            return getBzOffshoreExposureChartProps(config, data)
        }
      },
    }

    const getHedgeTraderVsBzOffshoreChartProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleLineChartProps(Context, {
        chartTitle: 'Hedge trade vs. Brazil offshore',
      })(config, formatSeriesNames(data))

    const getBzOffshoreExposureChartProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleLineChartProps(Context, {
        chartTitle: 'Brazil offshore exposure',
        convertToPercentages: Array(data.data.length).fill(true),
      })(config, formatSeriesNames(data))
  }

  export namespace Comparison {
    const CONTEXT_ID = 'equity:performance_attribution:comparison'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'exposure_delta_directional_bz_g7'
        | 'exposure_gross_directional_bz_g7'
        | 'exposure_delta_directional_bz'
        | 'exposure_delta_directional_g7'
        | 'exposure_gross_arbitrage_bz'
        | 'exposure_gross_arbitrage_g7'
      context: typeof CONTEXT_ID
      params: Partial<AxiosRequestConfig>
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const getChartData = (
      config: ChartConfig,
      requestModifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (
        conf
      ) => conf
    ) =>
      getCompassAPIChartData(config, (conf) =>
        requestModifier({ ...conf, ...config.params })
      )

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        chartTitle:
          config.chart === 'exposure_delta_directional_bz_g7'
            ? 'Exposure Delta: Directional Brazil and G7'
            : config.chart === 'exposure_gross_directional_bz_g7'
            ? 'Exposure Gross: Directional Brazil and G7'
            : config.chart === 'exposure_delta_directional_bz'
            ? 'Exposure Delta: Directional Brazil'
            : config.chart === 'exposure_delta_directional_g7'
            ? 'Exposure Delta: Directional G7'
            : config.chart === 'exposure_gross_arbitrage_bz'
            ? 'Exposure Gross: Arbitrage Brazil'
            : 'Exposure Gross: Arbitrage G7',
        convertToPercentages: Array(data.data.length).fill(true),
      })(config, formatSeriesNames(data))
  }

  export namespace Exposure {
    const CONTEXT_ID = 'equity:performance_attribution:exposure'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title: string
      chart: ExposureChartId
      fund_key: string
      start_date: string
      end_date: string
      area?: string
      strategy?: string
      sector?: string
      ticker?: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const FUNDS_CHARTS_PATHS: Record<ExposureChartId, string> = {
      historical_exposure_by_strategy: '/funds/historical-exposure-by-strategy',
      historical_exposure_by_sector: '/funds/historical-exposure-by-sector',
      historical_exposure_by_ticker: '/funds/historical-exposure-by-ticker',
    }

    const getChartData = async (config: ChartConfig) => {
      const response = (await axios({
        url:
          PERFORMANCE_ATTRIBUTION_CHARTS_PATH +
          FUNDS_CHARTS_PATHS[config.chart],
        method: 'POST',
        data: {
          fund_key: config.fund_key,
          start_date: config.start_date,
          end_date: config.end_date,
          ...(config.chart === 'historical_exposure_by_strategy'
            ? { area: config.area, strategy: config.strategy }
            : config.chart === 'historical_exposure_by_sector'
            ? { sector: config.sector }
            : config.chart === 'historical_exposure_by_ticker'
            ? {
                area: config.area,
                strategy: config.strategy,
                sector: config.sector,
                ticker: config.ticker,
              }
            : {}),
        },
      })) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: true,
        }),
        id: `series-${idx}`,
        name: capitalizeString(s.name),
        marker: { enabled: false },
        type: 'spline',
        tooltip: { valueDecimals: 2, valueSuffix: '%' },
        yAxis: idx === 0 ? 'first' : 'second',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const commonYAxis = {
        title: { text: '' },
        labels: { format: '{value}%' },
      }

      const yAxis = [
        { ...commonYAxis, id: 'first', opposite: false },
        { ...commonYAxis, id: 'second', opposite: true },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            colors: chartPalette,
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Attribution {
    const CONTEXT_ID = 'equity:performance_attribution:attribution'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title: string
      chart: AttributionChartId
      fund_key: string
      start_date: string
      end_date: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const FUNDS_CHARTS_PATHS: Record<AttributionChartId, string> = {
      unexplained_attribution: '/funds/unexplained-attribution',
      daily_attribution: '/funds/daily-attribution',
      historical_sector_exposure: '/funds/historical-sector-exposure',
      historical_sector_exposure_gross_up:
        '/funds/historical-sector-exposure-gross-up',
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          PERFORMANCE_ATTRIBUTION_CHARTS_PATH +
          FUNDS_CHARTS_PATHS[config.chart],
        method: 'POST',
        data: {
          fund_key: config.fund_key,
          start_date: config.start_date,
          end_date: config.end_date,
        },
      }).then((response) => response.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'historical_sector_exposure_gross_up':
            return getGrossUpChartProps(config, data)
          default:
            return getChartProps(config, data)
        }
      },
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        chartTitle: config.title,
        convertToPercentages: Array(data.data.length).fill(true),
        yAxis: [
          {
            id: 'default',
            labelSuffix: '%',
            ...(config.chart === 'historical_sector_exposure'
              ? { min: 0 }
              : {}),
          },
        ],
      })(config, formatSeriesNames(data))

    const getGrossUpChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleColumnChartProps(
        Context,
        {
          chartTitle: config.title,
          convertToPercentages: Array(data.data.length).fill(true),
          plotOptions: { area: { stacking: 'stacking' } },
          yAxis: [{ id: 'default', labelSuffix: '%', max: 100, min: 0 }],
        },
        { type: Array(data.data.length).fill('area') }
      )(config, formatSeriesNames(data))
  }

  export namespace TopBottom {
    const CONTEXT_ID = 'equity:performance_attribution:top_bottom'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      fund_key: string
      start_date: string
      end_date: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          PERFORMANCE_ATTRIBUTION_CHARTS_PATH + '/funds/top-bottom-by-sector',
        method: 'POST',
        data: {
          fund_key: config.fund_key,
          start_date: config.start_date,
          end_date: config.end_date,
        },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = [
        {
          id: `series-1`,
          type: 'column',
          name: '',
          data: data.data.map((s: any) => [s.sector, s.diff_exposure * 100]),
          dataLabels: { enabled: true, format: '{y:.2f}%' },
          showInLegend: false,
          yAxis: 'default',
        },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      const tooltip = {
        shared: true,
        split: undefined,
        valueDecimals: 2,
        valueSuffix: '%',
      }

      const xAxis = [{ categories: data.data.map((s: any) => s.sector) ?? [] }]

      const yAxis = [{ id: 'default', opposite: false, title: { text: '' } }]

      return {
        id: Context.getChartId(config),
        title: 'OW/UW IBOV',
        options: mergeDeepAll([
          creditsConfig,
          {
            colors: chartPalette,
            stockTools: { gui: { enabled: false } },
            chart: { type: 'bar' },
            series,
            tooltip,
            xAxis,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
